/**
 * Strategy-section seeds (starter-registry plan R3) — RDM `type: table`
 * documents through the SAME production stack as the flowchart seeds
 * (parseAndValidate → toLayoutedRuntimeModel → ApplyRdmSceneCommand;
 * tables skip ELK — RdmHydrator.tableDocumentToRuntimeModel docks
 * content into cells).
 *
 * Structures come from the registered table schemas (a table decl id —
 * or `schemaId:` prop — matching a visual schema resolves its grid:
 * tableSchemas.registry.ts). Tables without a registered schema use the
 * custom `columns:` config. `cell:` references resolve by header/cell
 * label. Content is deliberately small and recognizable — the
 * activation goal is "edit familiar content in <60s".
 */

import type { SeedProvider } from '../SeedRegistry';

const SWOT_BASIC_RDM = `---
type: table
title: "SWOT Analysis"
---
table swot "SWOT Analysis" { layout: stack }
sticky st_1 "Strong brand recognition" { cell: "Strengths" }
sticky st_2 "Experienced core team" { cell: "Strengths" }
sticky wk_1 "Single revenue stream" { cell: "Weaknesses" }
sticky wk_2 "Limited marketing budget" { cell: "Weaknesses" }
sticky op_1 "Growing demand in EMEA" { cell: "Opportunities" }
sticky op_2 "Partner channel untapped" { cell: "Opportunities" }
sticky th_1 "New low-cost competitor" { cell: "Threats" }
sticky th_2 "Key platform API changing" { cell: "Threats" }
`;

const BMC_BASIC_RDM = `---
type: table
title: "Business Model Canvas"
---
table bmc "Business Model Canvas" { schemaId: "business-model-canvas", layout: stack }
sticky kp_1 "Cloud hosting partner" { cell: "Key Partners" }
sticky ka_1 "Product development" { cell: "Key Activities" }
sticky kr_1 "Engineering team" { cell: "Key Resources" }
sticky vp_1 "10x faster setup than incumbents" { cell: "Value Propositions" }
sticky cr_1 "Self-serve with in-app support" { cell: "Customer Relationships" }
sticky ch_1 "Direct web + partner referrals" { cell: "Channels" }
sticky cs_1 "Mid-market operations teams" { cell: "Customer Segments" }
sticky co_1 "Hosting + people" { cell: "Cost Structure" }
sticky rs_1 "Subscriptions + add-ons" { cell: "Revenue Streams" }
`;

const OKR_CASCADE_RDM = `---
type: table
title: "OKR Cascade"
---
table okrs "OKR Cascade — this quarter" { columns: ["Company objective", "Team objectives", "Key results"], layout: stack }
sticky co_1 "Become the default tool for new teams" { cell: "Company objective" }
sticky to_1 "Grow self-serve signups 40%" { cell: "Team objectives" }
sticky to_2 "Cut onboarding to under 10 minutes" { cell: "Team objectives" }
sticky kr_1 "1,000 weekly activated teams" { cell: "Key results" }
sticky kr_2 "Activation rate 35% to 50%" { cell: "Key results" }
sticky kr_3 "NPS above 40" { cell: "Key results" }
`;

const COMPARISON_MATRIX_RDM = `---
type: table
title: "Comparison Matrix"
---
table compare "Comparison Matrix" { schemaId: "comparison", layout: stack }
sticky cr_1 "Price / total cost" { cell: "Criteria" }
sticky cr_2 "Time to deploy" { cell: "Criteria" }
sticky cr_3 "Security & compliance" { cell: "Criteria" }
sticky oa_1 "Lower cost, longer setup" { cell: "Option 1" }
sticky ob_1 "Premium, fastest deploy" { cell: "Option 2" }
sticky oc_1 "Mid-range, strongest compliance" { cell: "Option 3" }
`;

export const swotBasicSeed: SeedProvider = {
  id: 'swot-basic',
  type: 'flowchart',
  label: 'SWOT analysis',
  provide: () => ({ format: 'rdm', rdm: SWOT_BASIC_RDM }),
};

export const bmcBasicSeed: SeedProvider = {
  id: 'bmc-basic',
  type: 'flowchart',
  label: 'Business Model Canvas',
  provide: () => ({ format: 'rdm', rdm: BMC_BASIC_RDM }),
};

export const okrCascadeSeed: SeedProvider = {
  id: 'okr-cascade',
  type: 'flowchart',
  label: 'OKR cascade',
  provide: () => ({ format: 'rdm', rdm: OKR_CASCADE_RDM }),
};

export const comparisonMatrixSeed: SeedProvider = {
  id: 'comparison-matrix',
  type: 'flowchart',
  label: 'Comparison matrix',
  provide: () => ({ format: 'rdm', rdm: COMPARISON_MATRIX_RDM }),
};
